import { Col, Row } from "react-bootstrap";
import { useEffect, useState } from "react";
import { 
  LineChart, 
  Line, 
  BarChart, 
  Bar, 
  PieChart, 
  Pie, 
  ResponsiveContainer,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend,
  Cell
} from "recharts";
import {
  fetchWeeklyAttendance,
  fetchLeaveRequests,
  fetchHiringPipeline,
  fetchEmployeeDistribution,
  fetchProjectStatus,
  fetchAssetUtilization,
  fetchIncidentSeverity,
  WeeklyAttendanceData,
  LeaveRequestData,
  HiringPipelineData,
  ProjectStatusData,
  AssetUtilizationData,
  IncidentSeverityData,
  INCIDENT_SEVERITY_CONFIG,
} from "../services/DashboardDataService";

type DepartmentChartItem = {
  name: string;
  value: number;
};

const AnalyticsCharts = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [attendanceData, setAttendanceData] = useState<WeeklyAttendanceData | null>(null);
  const [leaveData, setLeaveData] = useState<LeaveRequestData | null>(null);
  const [hiringData, setHiringData] = useState<HiringPipelineData | null>(null);
  const [departmentData, setDepartmentData] = useState<DepartmentChartItem[]>([]);
  const [projectData, setProjectData] = useState<ProjectStatusData | null>(null);
  const [assetData, setAssetData] = useState<AssetUtilizationData[]>([]);
  const [incidentData, setIncidentData] = useState<IncidentSeverityData | null>(null);
  const isDark = document.documentElement.getAttribute("data-bs-theme") === "dark";
  const chartText = isDark ? "#E5E7EB" : "#374151";
  const chartGrid = isDark ? "#3F3F46" : "#D1D5DB";
    const tooltipBg = isDark ? "#1F2937" : "#FFFFFF";
    const tooltipBorder = isDark ? "#374151" : "#D1D5DB";
    const tooltipText = isDark ? "#FFFFFF" : "#111827";

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [
          weeklyAttendance,
          leaveRequests,
          hiringPipeline,
          departments,
          projectStatus,
          assetUtilization,
          incidentSeverity,
        ] = await Promise.all([
          fetchWeeklyAttendance(),
          fetchLeaveRequests(),
          fetchHiringPipeline(),
          fetchEmployeeDistribution(),
          fetchProjectStatus(),
          fetchAssetUtilization(),
          fetchIncidentSeverity(),
        ]);

        setAttendanceData(weeklyAttendance);
        setLeaveData(leaveRequests);
        setHiringData(hiringPipeline);
        setDepartmentData(departments);
        setProjectData(projectStatus);
        setAssetData(assetUtilization);
        setIncidentData(incidentSeverity);
      } catch (err) {
        console.error('Error loading analytics data:', err);
        setError('Failed to load analytics data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Transform data for charts
  const getAttendanceChartData = () => {
    if (!attendanceData) return [];
    return attendanceData.labels.map((label, index) => ({
      name: label,
      thisWeek: attendanceData.thisWeek[index] || 0,
      lastWeek: attendanceData.lastWeek[index] || 0,
    }));
  };

  const getLeaveChartData = () => {
    if (!leaveData) return [];
    return [
      { name: 'Approved', value: leaveData.approved, color: '#10b981' },
      { name: 'Pending', value: leaveData.pending, color: '#fbbf24' },
      { name: 'Rejected', value: leaveData.rejected, color: '#ef4444' },
    ];
  };

  const getHiringPipelineData = () => [
  {
    name: "Open",
    value: hiringData?.open || 0,
    color: "#10B981",
  },
  {
    name: "Closed",
    value: hiringData?.closed || 0,
    color: "#EF4444",
  },
  {
    name: "On Hold",
    value: hiringData?.onHold || 0,
    color: "#F59E0B",
  },
];

  const getDepartmentChartData = () => {
    return departmentData.map((dept) => ({
      name: dept.name,
      employees: dept.value,
    }));
  };

  const getProjectChartData = () => {
    if (!projectData) return [];
    return [
      { name: 'Active', value: projectData.active, color: '#10b981' },
      { name: 'On Hold', value: projectData.onHold, color: '#fbbf24' },
      { name: 'Completed', value: projectData.completed, color: '#3b82f6' },
      { name: 'Delayed', value: projectData.delayed, color: '#ef4444' },
    ];
  };

  const getAssetChartData = () => {
    return assetData.map(asset => ({
      name: asset.category,
      inUse: asset.inUse,
      available: asset.available,
    }));
  };

  const getIncidentChartData = () => {
    if (!incidentData) return [];
    return INCIDENT_SEVERITY_CONFIG.map(({ key, name, color }) => ({
      name,
      value: incidentData[key] ?? 0,
      color,
    }));
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip" style={{
          backgroundColor: tooltipBg,
          border: `1px solid ${tooltipBorder}`,
          borderRadius: '8px',
          padding: '12px',
          color: tooltipText,
        }}>
          <p style={{ margin: 0, fontSize: '12px', color: chartText }}>{label}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} style={{ margin: '4px 0 0', fontSize: '12px', color: entry.color }}>
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Row className="g-4">
        {[...Array(7)].map((_, index) => (
          <Col key={index} md={6} lg={12}>
            <div className="glass-card chart-card" style={{ height: 250 }}>
              <div className="chart-header">
                <div>
                  <div className="chart-title">Loading...</div>
                </div>
              </div>
            </div>
          </Col>
        ))}
      </Row>
    );
  }

  if (error) {
    return (
      <div className="glass-card p-4 text-center text-danger">
        {error}
      </div>
    );
  }

  return (
    <Row className="g-3">
      {/* Weekly Attendance */}
      {/* <Col lg={12}>
        <div className="glass-card chart-card">
          <div className="chart-header">
            <div>
              <div className="chart-title">Weekly Attendance Performance</div>
              <div className="chart-subtitle">Employee attendance comparison (Comming Soon)</div>
            </div>
          </div>
          <div style={{ height: 180 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={getAttendanceChartData()}>
                <CartesianGrid strokeDasharray="3 3" stroke={chartGrid} />
                <XAxis
                  dataKey="name"
                  tick={{
                    fill: chartText,
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                  axisLine={{
                    stroke: chartGrid,
                  }}
                  tickLine={{
                    stroke: chartGrid,
                  }}
                />
                <YAxis
                  tick={{
                    fill: chartText,
                    fontSize: 12,
                  }}
                  axisLine={{
                    stroke: chartGrid,
                  }}
                  tickLine={{
                    stroke: chartGrid,
                  }}
                  domain={[0, "dataMax + 5"]}
                  allowDecimals={false}
                />

                <Line
                  type="monotone"
                  dataKey="thisWeek"
                  stroke="#10b981"
                  strokeWidth={3}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />

                <Line
                  type="monotone"
                  dataKey="lastWeek"
                  stroke="#3b82f6"
                  strokeWidth={3}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </Col> */}

      {/* Leave Requests */}
      <Col md={6}>
        <div className="glass-card chart-card">
          <div className="chart-header">
            <div>
              <div className="chart-title">Leave Requests</div>
              <div className="chart-subtitle">Current leave status</div>
            </div>
          </div>
          <div style={{ height: 140 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={getLeaveChartData()}
                  cx="50%"
                  cy="50%"
                  innerRadius={45}
                  outerRadius={60}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {getLeaveChartData().map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend 
                  wrapperStyle={{
                    fontSize: '9px',
                    color: chartText,
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </Col>

      {/* Hiring Pipeline */}
      <Col md={6}>
        <div className="glass-card chart-card">
          <div className="chart-header">
            <div>
              <div className="chart-title">Hiring Pipeline</div>
              <div className="chart-subtitle">Candidate funnel</div>
            </div>
          </div>
          <div style={{ height: 140 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={getHiringPipelineData()} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke={chartGrid} />
                <XAxis 
                  type="number" 
                  stroke={chartText} 
                  fontSize={10}
                />
                <YAxis 
                  type="category" 
                  dataKey="name" 
                  stroke={chartText} 
                  fontSize={10}
                  width={60}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="value" radius={[0, 8, 8, 0]}>
                  {getHiringPipelineData().map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </Col>

      {/* Department Distribution */}
      <Col md={6}>
        <div className="glass-card chart-card">
          <div className="chart-header">
            <div>
              <div className="chart-title">Employee Distribution</div>
              <div className="chart-subtitle">By department</div>
            </div>
          </div>
          <div style={{ height: 140 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={getDepartmentChartData()}>
                <CartesianGrid strokeDasharray="3 3" stroke={chartGrid} />
                <XAxis
                  dataKey="name"
                  tick={{
                    fill: chartText,
                    fontSize: 10,
                    fontWeight: 600,
                  }}
                  axisLine={{
                    stroke: chartGrid,
                  }}
                  tickLine={{
                    stroke: chartGrid,
                  }}
                />
                <YAxis
                  tick={{
                    fill: chartText,
                    fontSize: 10,
                  }}
                  axisLine={{
                    stroke: chartGrid,
                  }}
                  tickLine={{
                    stroke: chartGrid,
                  }}
                  domain={[0, "dataMax + 5"]}
                  allowDecimals={false}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="employees" fill="#6366f1" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </Col>

      {/* Project Status
      <Col md={6}>
        <div className="glass-card chart-card">
          <div className="chart-header">
            <div>
              <div className="chart-title">Project Status</div>
              <div className="chart-subtitle">Current project overview</div>
            </div>
          </div>
          <div style={{ height: 200 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={getProjectChartData()}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {getProjectChartData().map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend 
                  wrapperStyle={{
                    fontSize: '11px',
                    color: chartText,
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </Col> */}

      {/* Asset Utilization */}
      <Col md={6}>
        <div className="glass-card chart-card">
          <div className="chart-header">
            <div>
              <div className="chart-title">Asset Utilization</div>
              <div className="chart-subtitle">Equipment status</div>
            </div>
          </div>
          <div style={{ height: 140 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={getAssetChartData()}>
                <CartesianGrid strokeDasharray="3 3" stroke={chartGrid} />
                <XAxis
                  dataKey="name"
                  tick={{
                    fill: chartText,
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                  axisLine={{
                    stroke: chartGrid,
                  }}
                  tickLine={{
                    stroke: chartGrid,
                  }}
                />
                <YAxis
                  tick={{
                    fill: chartText,
                    fontSize: 12,
                  }}
                  axisLine={{
                    stroke: chartGrid,
                  }}
                  tickLine={{
                    stroke: chartGrid,
                  }}
                  domain={[0, "dataMax + 5"]}
                  allowDecimals={false}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend 
                  wrapperStyle={{
                    fontSize: '11px',
                    color: chartText,
                  }}
                />
                <Bar dataKey="inUse" fill="#10b981" radius={[4, 4, 0, 0]} />
                <Bar dataKey="available" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </Col>

      {/* Incident Severity */}
      <Col md={6}>
        <div className="glass-card chart-card">
          <div className="chart-header">
            <div>
              <div className="chart-title">Incident Severity</div>
              <div className="chart-subtitle">Open incidents by priority</div>
            </div>
          </div>
          <div style={{ height: 140 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={getIncidentChartData()}
                  cx="50%"
                  cy="50%"
                  innerRadius={45}
                  outerRadius={60}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {getIncidentChartData().map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend 
                  wrapperStyle={{
                    fontSize: '9px',
                    color: chartText,
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </Col>
    </Row>
  );
};

export default AnalyticsCharts;
